import { useContext, useEffect, useRef, useState } from 'react';

import { SimulationContext, type SubSimulation } from 'contexts/SimulationContext';
import { GeodataContext } from 'contexts/GeodataContext';

import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';

import IconButton from '@mui/material/IconButton';
import { Grid, TextInput } from 'components/lib';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { ValidationContext } from 'contexts/ValidationContext';

import { DeleteIcon } from 'components/icons';
import { UMButton, UMInputNumber } from '@urbanmetrix/um-kit';
import {
  DEFAULT_LINE_SOURCE_SPACING_RATIO,
  DEFAULT_NOISE_SOURCE_DISTANCE,
  DEFAULT_MAX_REFLECTED_SOURCE_DISTANCE,
  MAX_NUMBER_OF_REFLECTIONS,
  MIN_NUMBER_OF_REFLECTIONS,
  MAX_REFLECTED_SOURCE_DISTANCE,
  MIN_REFLECTED_SOURCE_DISTANCE,
  DEFAULT_NUMBER_OF_REFLECTIONS,
  LINE_SOURCE_SPACING_RATIO_HIGH,
  LINE_SOURCE_SPACING_RATIO_LOW,
  LINE_SOURCE_SPACING_RATIO_NORMAL,
  LINE_SOURCE_SPACING_RATIO_SLIDER_VALUES,
  MAX_LINE_SOURCE_SPACING_RATIO,
  MAX_NOISE_SOURCE_DISTANCE_WITH_REFLECTION_AND_HORIZONTAL_DIFFRACTION,
  MIN_NOISE_SOURCE_DISTANCE,
} from 'constants/simulation';
import { getInputNumberRangeErrorMessage, isInputNumberInRange } from 'utils/inputNumberErrorMessage';

const lineSourceSpacingRatioBySliderValue: Record<number, number> = {
  0: LINE_SOURCE_SPACING_RATIO_LOW,
  50: LINE_SOURCE_SPACING_RATIO_NORMAL,
  100: LINE_SOURCE_SPACING_RATIO_HIGH,
};

const sliderValueByLineSourceSpacingRatio: Record<number, number> = {
  [LINE_SOURCE_SPACING_RATIO_LOW]: 0,
  [LINE_SOURCE_SPACING_RATIO_NORMAL]: 50,
  [LINE_SOURCE_SPACING_RATIO_HIGH]: 100,
};

const lineSourceSpacingRatioMarks = [
  { value: 0, label: 'Low' },
  { value: 50, label: 'Normal' },
  { value: 100, label: 'High' },
];

const noiseFilterItems = [
  { name: 'motorway', label: 'Motorway' },
  { name: 'trunk', label: 'Trunk' },
  { name: 'primary', label: 'Primary Streets' },
  { name: 'secondary', label: 'Secondary Streets' },
  { name: 'tertiary', label: 'Tertiary Streets' },
  { name: 'unclassified', label: 'Unclassified' },
  { name: 'residential', label: 'Residential Streets' },
  { name: 'service', label: 'Service Streets' },
];
const noiseFilters = noiseFilterItems.map((item) => item.name);

const Noise = () => {
  const [reflectionActiveById, setReflectionActiveById] = useState<Record<number, boolean>>({});
  const initializedNoiseDefaultsRef = useRef<Record<number, boolean>>({});
  const [manualLineSourceSpacingRatioById, setManualLineSourceSpacingRatioById] = useState<Record<number, boolean>>({});
  const { setIsSimulationNoiseSourcesValid, setSimulationNoiseSourcesErrorMessage } = useContext(ValidationContext);
  const { geodataParams } = useContext(GeodataContext);

  const { subSimulations, setSubSimulations, setSubSimulationParams, setSubSimulationName, defaultNoise } =
    useContext(SimulationContext);

  const toInputValue = (value: unknown): number | string | null => {
    if (value == null) return null;
    if (typeof value === 'number' || typeof value === 'string') return value;
    return null;
  };

  const toPositiveNumberOrZero = (value: unknown) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return 0;
    }
    return parsed;
  };

  const midTerrainDistance = toPositiveNumberOrZero(geodataParams?.lengthMid);
  const midTerrainResolution = toPositiveNumberOrZero(geodataParams?.resolutionMid);
  const maxNoiseSourceDistance = Math.max(0, midTerrainDistance - midTerrainResolution);
  const getEffectiveMaxNoiseSourceDistance = (subSimulation: SubSimulation) => {
    const numberOfReflection = Number(subSimulation.params?.numberOfReflection ?? DEFAULT_NUMBER_OF_REFLECTIONS);
    const isReflectionEnabled = Number.isFinite(numberOfReflection)
      ? numberOfReflection > DEFAULT_NUMBER_OF_REFLECTIONS
      : false;
    const isHorizontalDiffractionEnabled = Boolean(subSimulation.params?.horizontalDiffraction);

    if (isReflectionEnabled && isHorizontalDiffractionEnabled) {
      return Math.min(maxNoiseSourceDistance, MAX_NOISE_SOURCE_DISTANCE_WITH_REFLECTION_AND_HORIZONTAL_DIFFRACTION);
    }

    return maxNoiseSourceDistance;
  };

  useEffect(() => {
    setIsSimulationNoiseSourcesValid(true);
    setSimulationNoiseSourcesErrorMessage('');
  }, [setIsSimulationNoiseSourcesValid, setSimulationNoiseSourcesErrorMessage]);

  useEffect(() => {
    subSimulations
      .filter((subSim) => subSim.simType === 'NOISE')
      .forEach((subSim) => {
        if (initializedNoiseDefaultsRef.current[subSim.id]) return;

        initializedNoiseDefaultsRef.current[subSim.id] = true;

        const currentValue = subSim?.params?.maxSourceDistance;
        const shouldInitialize = currentValue == null || currentValue === '';
        const effectiveMaxNoiseSourceDistance = getEffectiveMaxNoiseSourceDistance(subSim);
        const defaultMaxNoiseSourceDistance = Math.min(DEFAULT_NOISE_SOURCE_DISTANCE, effectiveMaxNoiseSourceDistance);

        if (shouldInitialize) {
          setSubSimulationParams(subSim.id, { maxSourceDistance: defaultMaxNoiseSourceDistance });
        } else if (Number(currentValue) > effectiveMaxNoiseSourceDistance) {
          setSubSimulationParams(subSim.id, { maxSourceDistance: effectiveMaxNoiseSourceDistance });
        }

        const currentLineSourceSpacingRatio = subSim?.params?.lineSourceSpacingRatio;
        if (currentLineSourceSpacingRatio == null || currentLineSourceSpacingRatio === '') {
          setSubSimulationParams(subSim.id, { lineSourceSpacingRatio: DEFAULT_LINE_SOURCE_SPACING_RATIO });
        }
      });
  }, [maxNoiseSourceDistance, setSubSimulationParams, subSimulations]);

  useEffect(() => {
    setReflectionActiveById((previousReflectionActiveById) => {
      let hasChanges = false;
      const nextReflectionActiveById: Record<number, boolean> = {};

      subSimulations
        .filter((subSim) => subSim.simType === 'NOISE')
        .forEach((subSim) => {
          if (Object.prototype.hasOwnProperty.call(previousReflectionActiveById, subSim.id)) {
            nextReflectionActiveById[subSim.id] = previousReflectionActiveById[subSim.id];
            return;
          }

          const numberOfReflection = Number(subSim.params?.numberOfReflection ?? DEFAULT_NUMBER_OF_REFLECTIONS);
          nextReflectionActiveById[subSim.id] = Number.isFinite(numberOfReflection)
            ? numberOfReflection > DEFAULT_NUMBER_OF_REFLECTIONS
            : false;
          hasChanges = true;
        });

      if (Object.keys(nextReflectionActiveById).length !== Object.keys(previousReflectionActiveById).length) {
        hasChanges = true;
      }

      return hasChanges ? nextReflectionActiveById : previousReflectionActiveById;
    });
  }, [subSimulations]);

  useEffect(() => {
    setManualLineSourceSpacingRatioById((previousManualLineSourceSpacingRatioById) => {
      let hasChanges = false;
      const nextManualLineSourceSpacingRatioById: Record<number, boolean> = {};

      subSimulations
        .filter((subSim) => subSim.simType === 'NOISE')
        .forEach((subSim) => {
          if (Object.prototype.hasOwnProperty.call(previousManualLineSourceSpacingRatioById, subSim.id)) {
            nextManualLineSourceSpacingRatioById[subSim.id] = previousManualLineSourceSpacingRatioById[subSim.id];
            return;
          }

          const lineSourceSpacingRatio = Number(
            subSim.params?.lineSourceSpacingRatio ?? DEFAULT_LINE_SOURCE_SPACING_RATIO
          );
          nextManualLineSourceSpacingRatioById[subSim.id] =
            !LINE_SOURCE_SPACING_RATIO_SLIDER_VALUES.includes(lineSourceSpacingRatio);
          hasChanges = true;
        });

      if (
        Object.keys(nextManualLineSourceSpacingRatioById).length !==
        Object.keys(previousManualLineSourceSpacingRatioById).length
      ) {
        hasChanges = true;
      }

      return hasChanges ? nextManualLineSourceSpacingRatioById : previousManualLineSourceSpacingRatioById;
    });
  }, [subSimulations]);

  const handleInputsChange = (name: string | undefined, value: unknown, id: number) => {
    if (!name) return;
    setSubSimulationParams(id, { [name]: value });
  };

  const handleNameChange = (newName: string, id: number) => {
    setSubSimulationName(newName, id);
  };

  const handleFilters = (name: string, checked: boolean, id: number) => {
    const subSimulation = subSimulations.find((subSim) => subSim.id === id);
    const selectedFilters = (subSimulation?.params?.filters as string[] | undefined) || [];
    let filters = selectedFilters.length === 0 ? [...noiseFilters] : [...selectedFilters];

    if (checked && !filters.includes(name)) filters = [...filters, name];
    else filters = filters.filter((filter) => filter !== name && (name !== 'motorway' || filter !== 'motorway_link'));

    handleInputsChange('filters', filters.length === noiseFilters.length ? [] : filters, id);
  };

  const resolveLineSourceSpacingRatioSliderValue = (value: unknown) => {
    const numericValue = Number(value);
    return (
      sliderValueByLineSourceSpacingRatio[numericValue] ??
      sliderValueByLineSourceSpacingRatio[DEFAULT_LINE_SOURCE_SPACING_RATIO]
    );
  };

  const handleLineSourceSpacingRatioSliderChange = (_: Event, sliderValue: number | number[], id: number) => {
    const resolvedSliderValue = Array.isArray(sliderValue) ? sliderValue[0] : sliderValue;
    const nextValue = lineSourceSpacingRatioBySliderValue[resolvedSliderValue] ?? DEFAULT_LINE_SOURCE_SPACING_RATIO;
    handleInputsChange('lineSourceSpacingRatio', nextValue, id);
  };

  const handleManualLineSourceSpacingRatioToggle = (checked: boolean, subSimulation: SubSimulation) => {
    setManualLineSourceSpacingRatioById((previousManualLineSourceSpacingRatioById) => ({
      ...previousManualLineSourceSpacingRatioById,
      [subSimulation.id]: checked,
    }));

    if (!checked) {
      const currentValue = Number(subSimulation.params?.lineSourceSpacingRatio ?? DEFAULT_LINE_SOURCE_SPACING_RATIO);
      const sliderValue = resolveLineSourceSpacingRatioSliderValue(currentValue);
      const nextValue = lineSourceSpacingRatioBySliderValue[sliderValue] ?? DEFAULT_LINE_SOURCE_SPACING_RATIO;
      handleInputsChange('lineSourceSpacingRatio', nextValue, subSimulation.id);
    }
  };

  const removeBlock = (id: number) => {
    setSubSimulations(subSimulations.filter((block) => block.id !== id));
    setReflectionActiveById((previousReflectionActiveById) => {
      const nextReflectionActiveById = { ...previousReflectionActiveById };
      delete nextReflectionActiveById[id];
      return nextReflectionActiveById;
    });
    setManualLineSourceSpacingRatioById((previousManualLineSourceSpacingRatioById) => {
      const nextManualLineSourceSpacingRatioById = { ...previousManualLineSourceSpacingRatioById };
      delete nextManualLineSourceSpacingRatioById[id];
      return nextManualLineSourceSpacingRatioById;
    });
  };

  const addNoiseSimulation = () => {
    const newId =
      subSimulations.length > 0 ? Math.max(...subSimulations.map((subSimulation) => subSimulation.id)) + 1 : 0;
    setSubSimulations([...subSimulations, defaultNoise(newId)]);
  };

  return (
    <>
      {subSimulations
        .filter((subSim) => subSim.simType === 'NOISE')
        .map((subSimulation, index) => {
          const numberOfReflection = Number(subSimulation.params?.numberOfReflection ?? DEFAULT_NUMBER_OF_REFLECTIONS);
          const isReflectionActive =
            reflectionActiveById[subSimulation.id] ??
            (Number.isFinite(numberOfReflection) && numberOfReflection > DEFAULT_NUMBER_OF_REFLECTIONS);
          const effectiveMaxNoiseSourceDistance = getEffectiveMaxNoiseSourceDistance(subSimulation);
          const defaultMaxNoiseSourceDistance = Math.min(
            DEFAULT_NOISE_SOURCE_DISTANCE,
            effectiveMaxNoiseSourceDistance
          );
          const isManualLineSourceSpacingRatio =
            manualLineSourceSpacingRatioById[subSimulation.id] ??
            !LINE_SOURCE_SPACING_RATIO_SLIDER_VALUES.includes(
              Number(subSimulation.params?.lineSourceSpacingRatio ?? DEFAULT_LINE_SOURCE_SPACING_RATIO)
            );

          return (
            <div key={index}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '16px',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '24px',
                  }}
                >
                  <div style={{ width: '50%', minWidth: 0 }}>
                    <TextInput
                      label="Simulation Name"
                      name={`name`}
                      value={subSimulation?.name || ''}
                      onChange={(_name, value) => handleNameChange(value, subSimulation.id)}
                    />
                  </div>
                  <div style={{ width: '50%', minWidth: 0 }}>
                    <UMInputNumber
                      label={
                        <span className="grid-inline-label-with-info">
                          <span>Max Noise Sources Distance [m]</span>
                          <CustomTooltip
                            title={'Max value is Intermediate Terrain distance minus Intermediate Terrain resolution.'}
                          />
                        </span>
                      }
                      name="maxSourceDistance"
                      width={84}
                      maxErrorMessageWidth={276}
                      value={toInputValue(subSimulation.params?.maxSourceDistance) ?? defaultMaxNoiseSourceDistance}
                      onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                      defaultValue={defaultMaxNoiseSourceDistance}
                      min={MIN_NOISE_SOURCE_DISTANCE}
                      max={effectiveMaxNoiseSourceDistance}
                      valid={isInputNumberInRange(
                        subSimulation.params?.maxSourceDistance,
                        MIN_NOISE_SOURCE_DISTANCE,
                        effectiveMaxNoiseSourceDistance
                      )}
                      errorMessage={getInputNumberRangeErrorMessage(
                        MIN_NOISE_SOURCE_DISTANCE,
                        effectiveMaxNoiseSourceDistance
                      )}
                    />
                  </div>
                </div>
                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                  <IconButton size="large" color="error" onClick={() => removeBlock(subSimulation.id)}>
                    <DeleteIcon />
                  </IconButton>
                </div>
              </div>

              <Box sx={{ mt: 2.5, mb: -2 }}>
                <Typography variant="caption">
                  <span className="grid-inline-label-with-info">
                    <span>Emitter density</span>
                    <CustomTooltip
                      title={
                        'Lower values are faster, while higher values increase emitter density along road sources.'
                      }
                    />
                  </span>
                </Typography>
              </Box>

              <br />
              <FormControlLabel
                className="checkbox-row"
                control={
                  <Checkbox
                    checked={Boolean(isManualLineSourceSpacingRatio)}
                    onChange={(e) => handleManualLineSourceSpacingRatioToggle(e.target.checked, subSimulation)}
                  />
                }
                label={<>Set emitter density manually (advanced)</>}
              />

              {!isManualLineSourceSpacingRatio && (
                <Box sx={{ width: '100%', maxWidth: '220px' }}>
                  <Slider
                    onChange={(event, sliderValue) =>
                      handleLineSourceSpacingRatioSliderChange(event, sliderValue, subSimulation.id)
                    }
                    min={0}
                    max={100}
                    step={50}
                    sx={{ marginLeft: '15px', marginBottom: '30px' }}
                    value={resolveLineSourceSpacingRatioSliderValue(
                      subSimulation.params?.lineSourceSpacingRatio ?? DEFAULT_LINE_SOURCE_SPACING_RATIO
                    )}
                    marks={lineSourceSpacingRatioMarks}
                  />
                </Box>
              )}

              {isManualLineSourceSpacingRatio && (
                <Grid cols="3">
                  <UMInputNumber
                    label="Line Source Spacing Ratio"
                    name="lineSourceSpacingRatio"
                    width={84}
                    maxErrorMessageWidth={276}
                    value={toInputValue(subSimulation.params?.lineSourceSpacingRatio)}
                    onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                    defaultValue={DEFAULT_LINE_SOURCE_SPACING_RATIO}
                    min={LINE_SOURCE_SPACING_RATIO_LOW}
                    max={MAX_LINE_SOURCE_SPACING_RATIO}
                    valid={isInputNumberInRange(
                      subSimulation.params?.lineSourceSpacingRatio,
                      LINE_SOURCE_SPACING_RATIO_LOW,
                      MAX_LINE_SOURCE_SPACING_RATIO
                    )}
                    errorMessage={getInputNumberRangeErrorMessage(
                      LINE_SOURCE_SPACING_RATIO_LOW,
                      MAX_LINE_SOURCE_SPACING_RATIO
                    )}
                  />
                </Grid>
              )}

              <Typography variant="subtitle1" color="#1976d2">
                Types of noise sources to be included
                <span className="asterisk-color"> (* at least one)</span>
                <CustomTooltip
                  title={
                    'To inspect the type of noise sources around your site go on openstreetmap and right-click on the streets > query features'
                  }
                />
              </Typography>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  columnGap: '4px',
                  rowGap: '0px',
                }}
              >
                {noiseFilterItems.map((item) => (
                  <FormControlLabel
                    key={item.name}
                    className="checkbox-row"
                    control={
                      <Checkbox
                        checked={
                          ((subSimulation.params?.filters as string[] | undefined) || []).length === 0 ||
                          ((subSimulation.params?.filters as string[] | undefined) || []).includes(item.name)
                        }
                        onChange={(e) => handleFilters(item.name, e.target.checked, subSimulation.id)}
                      />
                    }
                    label={item.label}
                  />
                ))}
              </div>

              <Box
                component="details"
                sx={{
                  mt: 2,
                  mb: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Box
                  component="summary"
                  sx={{
                    cursor: 'pointer',
                    color: '#1976d2',
                    fontWeight: 600,
                  }}
                >
                  Advanced sound rays options
                </Box>
                <Typography variant="body2" color="warning.main" sx={{ mt: 1.5, mb: 2 }}>
                  Beta: reflection and diffraction settings may not work as expected.
                </Typography>

                <Typography variant="subtitle1" color="#1976d2">
                  <span className="grid-inline-label-with-info">
                    <span>Include also reflected sound rays</span>
                    <CustomTooltip
                      title={
                        'Includes also the rays that arrive to the sensor point via the reflection on obstacles (terrain and buildings).'
                      }
                    />
                  </span>
                </Typography>
                <Grid cols="4">
                  <FormControlLabel
                    className="checkbox-row"
                    control={
                      <Checkbox
                        checked={Boolean(isReflectionActive)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setReflectionActiveById((previousReflectionActiveById) => ({
                            ...previousReflectionActiveById,
                            [subSimulation.id]: checked,
                          }));

                          if (!checked) {
                            handleInputsChange('numberOfReflection', DEFAULT_NUMBER_OF_REFLECTIONS, subSimulation.id);
                          } else {
                            handleInputsChange('numberOfReflection', MIN_NUMBER_OF_REFLECTIONS, subSimulation.id);
                          }
                        }}
                      />
                    }
                    label="Activate reflection"
                  />

                  {isReflectionActive && (
                    <>
                      <UMInputNumber
                        label="Max Number Of Reflections"
                        name="numberOfReflection"
                        width={84}
                        maxErrorMessageWidth={276}
                        value={toInputValue(subSimulation.params?.numberOfReflection)}
                        onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                        defaultValue={0}
                        min={MIN_NUMBER_OF_REFLECTIONS}
                        max={MAX_NUMBER_OF_REFLECTIONS}
                      />

                      <UMInputNumber
                        label="Max Reflected Source Distance [m]"
                        name="maxReflectionDistance"
                        width={84}
                        maxErrorMessageWidth={276}
                        value={toInputValue(subSimulation.params?.maxReflectionDistance)}
                        onChange={(name, value) => handleInputsChange(name, value, subSimulation.id)}
                        defaultValue={DEFAULT_MAX_REFLECTED_SOURCE_DISTANCE}
                        min={MIN_REFLECTED_SOURCE_DISTANCE}
                        max={MAX_REFLECTED_SOURCE_DISTANCE}
                      />
                    </>
                  )}
                </Grid>

                <Typography variant="subtitle1" color="#1976d2">
                  <span className="grid-inline-label-with-info">
                    <span>Include also diffracted sound rays</span>
                    <CustomTooltip
                      title={
                        'Includes also the rays that arrive to the sensor point via the diffraction around obstacle corners (terrian and buildings)'
                      }
                    />
                  </span>
                </Typography>
                <FormControlLabel
                  className="checkbox-row"
                  control={
                    <Checkbox
                      checked={Boolean(subSimulation.params?.verticalDiffraction)}
                      onChange={(e) => {
                        handleInputsChange('verticalDiffraction', e.target.checked, subSimulation.id);
                      }}
                    />
                  }
                  label="Diffracted Vertically Around Obstacles"
                />
              </Box>
              <Divider />
              <br />
            </div>
          );
        })}
      <UMButton text="Add sim" onClick={addNoiseSimulation} className="glass-btn-1" />
    </>
  );
};

export default Noise;
